/**
 * CTASection Component — Case Associates LP Override
 *
 * Overrides the base landing-page CTASection to style the contact button
 * with bg-primary and a white border.
 */

import {
  useContentToken,
  EditableToken,
} from '@vibesites/runtime/react';
import type { ComponentContentSchema } from '@vibesites/runtime';
// @ts-ignore
import { PhoneIcon, ChevronRightIcon } from '@vibesites/assets';

export function CTASection() {
  const headline = useContentToken('ctasection.headline');
  const phone = useContentToken('ctasection.phone');
  const buttonText = useContentToken('ctasection.button_text');
  const buttonUrl = useContentToken('ctasection.button_url');

  return (
    <section className="bg-secondary py-10 md:py-12 font-body">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 flex flex-col items-center gap-4">
        <EditableToken path="ctasection.headline" type="text" label="Headline" as="div">
          <p className="font-heading text-lg md:text-xl font-semibold text-secondary-foreground m-0 text-center">
            {headline}
          </p>
        </EditableToken>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-10 mt-1">
          {/* Phone */}
          <EditableToken path="ctasection.phone" type="phone" label="Phone Number" as="div">
            <a href={`tel:${phone}`} className="flex items-center gap-3 text-2xl md:text-[2rem] font-medium font-accent text-secondary-foreground no-underline">
              <PhoneIcon size={22} className="opacity-70" />
              {phone}
            </a>
          </EditableToken>

          {/* Contact Button */}
          <EditableToken path="ctasection.button_url" type="url" label="Button URL" as="span">
            <EditableToken path="ctasection.button_text" type="text" label="Button Text" as="span">
              <a
                href={buttonUrl || '#contact'}
                className="inline-flex items-center justify-center gap-2 py-4 px-10 bg-primary text-white border-2 border-white no-underline font-semibold font-body text-base rounded-md transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 active:scale-[0.98]"
              >
                {buttonText}
                <ChevronRightIcon size={16} />
              </a>
            </EditableToken>
          </EditableToken>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CTASection',
  displayName: 'Call to Action',
  tokens: {
    'ctasection.headline': {
      label: 'Headline',
      type: 'text',
      defaultValue: 'Ready to find your new home?',
    },
    'ctasection.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_phone',
      defaultValue: '555-555-5555',
    },
    'ctasection.button_text': {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'Contact Us',
    },
    'ctasection.button_url': {
      label: 'Button URL',
      type: 'url',
      defaultValue: '#contact',
    },
  },
};

(CTASection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default CTASection;
